'use client';

import usePrice from '@framework/product/use-price';
import cn from 'classnames';
import {useCart} from '@contexts/cart/cart.context';
import Button from '@components/ui/button';
import {CheckoutItem} from '@components/checkout/checkout-card-item';
import {CheckoutCardFooterItem} from './checkout-card-footer-item';
import {useNavigate} from 'react-router-dom';
import {ROUTES} from '@utils/routes';
import {useTranslation} from 'src/app/i18n/client';
import {useIsMounted} from '@utils/use-is-mounted';
import {useEffect, useState} from 'react';
import SearchResultLoader from '@components/ui/loaders/search-result-loader';
import {useCheckoutMutation} from "@framework/checkout/use-checkout";
import {Order} from "@framework/types";
import {CheckBox} from "@components/ui/form/checkbox";
import {Trans} from "react-i18next";
import {CheckoutDetailsCompletedProps} from "@components/checkout/checkout-details";

const CheckoutCard: React.FC<{
  lang: string, checkoutDetails: CheckoutDetailsCompletedProps
}> = ({
        lang,
        checkoutDetails
      }) => {
  const { t } = useTranslation(lang, 'common');
  const navigate = useNavigate();
  const [isLoading, setLoading] = useState(true);
  const {mutate: submitOrder, isPending: submittingOrder} = useCheckoutMutation()

  const [privacyCheckBoxSelected, setPrivacyCheckBoxSelected] = useState<boolean>()
  const [termsCheckBoxSelected, setTermsCheckBoxSelected] = useState<boolean>()
  const [deliveryCostsCheckboxSelected, setDeliveryCostsCheckboxSelected] = useState<boolean>()

  useEffect(() => {
    setLoading(false);
  }, []);
  const {items, total, isEmpty, resetCart} = useCart();
  const { price: subtotal } = usePrice({
    amount: total,
    currencyCode: 'EUR',
  });

  function handleSubmitOrder() {
    if (isEmpty) {
      return
    }

    submitOrder({items, ...checkoutDetails}, {
      onSuccess: (order: Order) => {
        resetCart()
        navigate(`/${lang}${ROUTES.ORDERS}/${order.id}`)
      }
    })

  }
  const checkoutFooter = [
    // {
    //   id: 1,
    //   name: t('text-sub-total'),
    //   price: subtotal,
    // },
    // {
    //   id: 2,
    //   name: t('text-shipping'),
    //   price: '$0',
    // },
    {
      id: 3,
      name: t('text-total'),
      price: subtotal,
    },
  ];
  const mounted = useIsMounted();
  return (
    <>
      <div className="px-4 pt-4 border rounded-md border-border-base text-brand-light xl:py-6 xl:px-7">
        <div className="flex pb-2 text-sm font-semibold rounded-md text-heading" style={{columnGap: 15}}>
          <div className="font-medium text-15px text-brand-dark" style={{marginLeft: 128, marginRight: 14}}>SKU</div>
          <span className="font-medium text-15px text-brand-dark">
            {t('text-product')}
          </span>
          <span className="font-medium ltr:ml-auto rtl:mr-auto shrink-0 text-15px text-brand-dark"
                style={{marginRight: 45}}>
            {t('Price Per Unit')}
          </span>
          <span className="font-medium shrink-0 text-15px text-brand-dark"
                style={{marginRight: 45}}>
            {t('text-quantity')}
          </span>
          <span className="font-medium shrink-0 text-15px text-brand-dark" style={{width: 90}}>
            {t('text-sub-total')}
          </span>
        </div>
        {isLoading ? (
            <div className="w-full">
              <SearchResultLoader uniqueKey={`product-key`}/>
            </div>
        ) : !isEmpty && mounted ? (
            items.map((item) => <CheckoutItem item={item} lang={lang} key={item.id}/>)
        ) : (
          <p className="py-4 text-brand-danger text-opacity-70">
            {t('text-empty-cart')}
          </p>
        )}
        {mounted && checkoutFooter.map((item: any) => (
            <CheckoutCardFooterItem item={item} key={item.id} />
          ))}
        <CheckBox lang={lang}
                  required={true}
                  checked={termsCheckBoxSelected}
                  onClick={() => setTermsCheckBoxSelected(!termsCheckBoxSelected)}
                  key="terms-checkbox">
          <Trans t={t} i18nKey="text-checkout-read-privacy-checkbox"
                 components={{
                   LinkC:
                       <a target="_blank" href={`/${lang}${ROUTES.PRIVACY}`}
                          className="font-medium underline text-brand">
                         {t('text-privacy')}
                       </a>
                 }}/>
        </CheckBox>
        <CheckBox lang={lang}
                  required={true}
                  checked={privacyCheckBoxSelected}
                  onClick={() => setPrivacyCheckBoxSelected(!privacyCheckBoxSelected)}
                  key="privacy-checkbox">
          <Trans
              t={t}
              i18nKey="text-checkout-read-terms-checkbox"
              components={{
                LinkC:
                    <a target="_blank" href={`/${lang}${ROUTES.TERMS}`} className="font-medium underline text-brand">
                      {t('text-terms-of-service')}{' '}
                    </a>
              }}/>
        </CheckBox>
        <CheckBox lang={lang}
                  required={true}
                  checked={deliveryCostsCheckboxSelected}
                  onClick={() => setDeliveryCostsCheckboxSelected(!deliveryCostsCheckboxSelected)}
                  key="privacy-checkbox">
          {t('text-checkout-delivery-costs')}
        </CheckBox>
        <Button
          variant="formButton"
          className={cn(
            'w-full mt-8 mb-5 rounded font-semibold px-4 py-3 transition-all',
            mounted && isEmpty
              ? 'opacity-40 cursor-not-allowed'
              : '!bg-brand !text-brand-light',
          )}
          disabled={submittingOrder || !privacyCheckBoxSelected || !termsCheckBoxSelected || !deliveryCostsCheckboxSelected}
          loading={submittingOrder}
          onClick={handleSubmitOrder}
        >
          {t('button-order-now')}
        </Button>
      </div>
    </>
  );
};

export default CheckoutCard;
